import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, ArrowRight, Check, Info, Sparkles, Upload, X, FileText, Image, File } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';
import TabNavigation from '../../components/TabNavigation';

const DIMENSIONS = [
  'Leadership',
  'Systems',
  'Risk',
  'Competence',
  'Measurement',
  'Culture',
  'Wellbeing',
  'Governance'
];

const SSEMAssessmentWizard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const { activeSiteId, activeSiteName } = useSite();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [periodTag, setPeriodTag] = useState('');
  const [assessmentId, setAssessmentId] = useState(null);
  const [descriptors, setDescriptors] = useState({});
  const [scores, setScores] = useState({});
  const [predictedScores, setPredictedScores] = useState({});
  const [evidenceNotes, setEvidenceNotes] = useState({});
  const [showPeriodModal, setShowPeriodModal] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({}); // { dimension: [{ id, filename, ... }] }
  const [uploading, setUploading] = useState(false);
  const [fetchingDescriptors, setFetchingDescriptors] = useState(false);

  const currentDimension = DIMENSIONS[currentStep];

  useEffect(() => {
    if (currentDimension && !descriptors[currentDimension]) {
      fetchDescriptors(currentDimension);
    }
  }, [currentDimension]);

  const fetchDescriptors = async (dimension) => {
    setFetchingDescriptors(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/ssem/descriptors?dimension=${dimension}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDescriptors(prev => ({ ...prev, [dimension]: response.data }));
    } catch (error) {
      console.error('Error fetching descriptors:', error);
    } finally {
      setFetchingDescriptors(false);
    }
  };

  const createAssessment = async () => {
    if (!activeSiteId) {
      showError('Please select a site from the header first.');
      return;
    }
    if (!periodTag.trim()) {
      showWarning('Please enter a period tag (e.g., Q1_2025)');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ssem/assessments`,
        { period_tag: periodTag, site_id: activeSiteId },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      setAssessmentId(response.data.id);

      // Fetch predicted scores
      const predictionsResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ssem/assessments/${response.data.id}/calculate-predictions`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPredictedScores(predictionsResponse.data.predicted_scores || {});
      setShowPeriodModal(false);
    } catch (error) {
      if (error.response?.data?.detail) {
        showError(error.response.data.detail);
      } else {
        showError('Error creating assessment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleScoreSelect = (dimension, score) => {
    setScores(prev => ({ ...prev, [dimension]: score }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const token = localStorage.getItem('token');
    const allowedTypes = ['.pdf', '.docx', '.xlsx', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.xls'];
    const maxSize = 50 * 1024 * 1024; // 50MB

    setUploading(true);

    for (const file of files) {
      // Validate file type
      const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!allowedTypes.includes(fileExt)) {
        showWarning(`File type ${fileExt} not allowed. Allowed: ${allowedTypes.join(', ')}`);
        continue;
      }

      // Validate file size
      if (file.size > maxSize) {
        showWarning(`File "${file.name}" exceeds 50MB limit`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('module', 'ssem');
        formData.append('reference_id', assessmentId);
        formData.append('description', `SSEM Evidence for ${currentDimension}`);
        formData.append('tags', `ssem,evidence,${currentDimension}`);

        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/files/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        // Add uploaded file to state
        setUploadedFiles(prev => ({
          ...prev,
          [currentDimension]: [
            ...(prev[currentDimension] || []),
            {
              id: response.data.id,
              filename: response.data.original_filename || response.data.filename,
              file_size: response.data.file_size,
              content_type: response.data.content_type
            }
          ]
        }));
      } catch (error) {
        console.error('File upload error:', error);
        showError(`Failed to upload "${file.name}": ${error.response?.data?.detail || 'Upload failed'}`);
      }
    }

    setUploading(false);
    // Clear the input
    e.target.value = '';
  };

  const removeUploadedFile = async (dimension, fileId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUploadedFiles(prev => ({
        ...prev,
        [dimension]: (prev[dimension] || []).filter(f => f.id !== fileId)
      }));
    } catch (error) {
      console.error('Error removing file:', error);
      showError('Failed to remove file');
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) {
      return <Image className="w-4 h-4 text-green-600" />;
    } else if (contentType?.includes('pdf')) {
      return <FileText className="w-4 h-4 text-red-600" />;
    } else {
      return <File className="w-4 h-4 text-blue-600" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const saveDimensionScore = async () => {
    if (!scores[currentDimension]) {
      showWarning('Please select a maturity level');
      return;
    }

    // Validate evidence notes
    const notes = evidenceNotes[currentDimension] || '';
    if (!notes.trim()) {
      showWarning('Please provide evidence notes to justify your selected maturity level. This ensures data-driven, credible assessments.');
      return;
    }

    if (notes.trim().length < 20) {
      showWarning('Evidence notes must be at least 20 characters. Please provide sufficient detail about your evidence.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Get uploaded file IDs for this dimension
      const dimensionFiles = uploadedFiles[currentDimension] || [];
      const evidenceUrls = dimensionFiles.map(f => f.id);

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ssem/assessments/${assessmentId}/dimensions`,
        {
          dimension: currentDimension,
          user_score: scores[currentDimension],
          evidence_urls: evidenceUrls,
          notes: notes
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      if (currentStep < DIMENSIONS.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        // All dimensions scored - generate roadmap
        await generateRoadmap();
      }
    } catch (error) {
      showError('Error saving score. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateRoadmap = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ssem/assessments/${assessmentId}/generate-roadmap`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Navigate to roadmap
      navigate(`/ssem/roadmap/${assessmentId}`);
    } catch (error) {
      console.error('Error generating roadmap:', error);
      showWarning('Assessment completed but roadmap generation failed. You can view it from the dashboard.');
      navigate('/ssem');
    } finally {
      setGenerating(false);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      1: 'border-red-300 bg-red-50',
      2: 'border-orange-300 bg-orange-50',
      3: 'border-yellow-300 bg-yellow-50',
      4: 'border-blue-300 bg-blue-50',
      5: 'border-indigo-300 bg-indigo-50',
      6: 'border-green-300 bg-green-50'
    };
    return colors[level] || 'border-gray-300 bg-gray-50';
  };

  const getLevelName = (level) => {
    const names = {
      1: 'Base',
      2: 'Defined',
      3: 'Practiced',
      4: 'Measured',
      5: 'Pervasive',
      6: 'Safety as a Value'
    };
    return names[level] || '';
  };

  if (showPeriodModal) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Start New SSEM Assessment</h2>
          <p className="text-gray-600 mb-6">Enter the period for this assessment (e.g., Q1_2025, Q2_2025)</p>
          
          {/* Site (auto-set from header site switcher) */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Site <span className="text-red-500">*</span>
            </label>
            {activeSiteId ? (
              <div className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                {activeSiteName || 'Loading...'}
              </div>
            ) : (
              <div className="w-full px-4 py-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm">
                Please select a site from the header first.
              </div>
            )}
          </div>

          <input
            type="text"
            placeholder="Q1_2025"
            value={periodTag}
            onChange={(e) => setPeriodTag(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
          />

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/ssem')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={createAssessment}
              disabled={loading || !activeSiteId}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start Assessment'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (generating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-16 h-16 text-blue-600 animate-pulse mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Generating AI Roadmap</h2>
          <p className="text-gray-600">Analyzing your assessment and creating improvement recommendations...</p>
        </div>
      </div>
    );
  }

  const dimensionDescriptors = descriptors[currentDimension] || [];
  const predictedScore = predictedScores[currentDimension];
  const selectedScore = scores[currentDimension];

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/ssem')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Cancel Assessment
          </Button>
          
          <h1 className="text-3xl font-bold text-gray-900">SSEM Assessment Wizard</h1>
          <p className="text-gray-600 mt-1">Period: {periodTag}</p>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Step {currentStep + 1} of {DIMENSIONS.length}: {currentDimension}
            </h2>
            <span className="text-sm text-gray-600">
              {Math.round(((currentStep + 1) / DIMENSIONS.length) * 100)}% Complete
            </span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / DIMENSIONS.length) * 100}%` }}
            />
          </div>

          <div className="flex justify-between mt-4">
            {DIMENSIONS.map((dim, idx) => (
              <div key={dim} className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  idx < currentStep ? 'bg-green-500 text-white' :
                  idx === currentStep ? 'bg-blue-600 text-white' :
                  'bg-gray-200 text-gray-600'
                }`}>
                  {idx < currentStep ? <Check className="w-4 h-4" /> : idx + 1}
                </div>
                <span className="text-xs text-gray-600 mt-1 hidden md:block">{dim}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Predicted Score Alert */}
        {predictedScore && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900 mb-1">Predicted Score: Level {predictedScore}</h3>
              <p className="text-sm text-blue-700">
                Based on your organization data, the maximum score for this dimension is capped at Level {predictedScore}. 
                This ensures data-driven, realistic assessments.
              </p>
            </div>
          </div>
        )}

        {/* Maturity Level Selection */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">Select Maturity Level for {currentDimension}</h2>
            <p className="text-sm text-gray-600 mt-1">Review the descriptors and select the level that best matches your organization</p>
          </div>

          <div className="p-6 space-y-4">
            {/* Loading State */}
            {fetchingDescriptors && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
                <p className="text-gray-600">Loading maturity levels...</p>
              </div>
            )}

            {/* Empty State - No Descriptors */}
            {!fetchingDescriptors && dimensionDescriptors.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <Info className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Maturity Levels</h3>
                <p className="text-gray-600 mb-4 max-w-md">
                  There was a problem loading the maturity level descriptors.
                  Please try again or contact support if the issue persists.
                </p>
                <Button
                  onClick={() => fetchDescriptors(currentDimension)}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Retry Loading
                </Button>
              </div>
            )}

            {/* Maturity Level Cards */}
            {!fetchingDescriptors && dimensionDescriptors.map((desc) => {
              const isSelected = selectedScore === desc.level;
              const isDisabled = predictedScore && desc.level > predictedScore;

              return (
                <div
                  key={desc.id}
                  onClick={() => !isDisabled && handleScoreSelect(currentDimension, desc.level)}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    isSelected ? 'border-blue-500 bg-blue-50' :
                    isDisabled ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed' :
                    `${getLevelColor(desc.level)} hover:shadow-md`
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        isSelected ? 'bg-blue-600 text-white' :
                        isDisabled ? 'bg-gray-300 text-gray-600' :
                        'bg-white border-2'
                      }`}>
                        {desc.level}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{desc.level_name}</h3>
                        {isDisabled && (
                          <span className="text-xs text-gray-500">Exceeds predicted score</span>
                        )}
                      </div>
                    </div>
                    {isSelected && <Check className="w-6 h-6 text-blue-600" />}
                  </div>

                  <p className="text-sm text-gray-700 mb-3">{desc.description_text}</p>

                  <div className="bg-white border border-gray-200 rounded p-3">
                    <p className="text-xs font-medium text-gray-600 mb-1">Evidence Required:</p>
                    <p className="text-xs text-gray-700">{desc.evidence_requirement}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Evidence Upload Section */}
          <div className="p-6 border-t bg-gradient-to-r from-amber-50 to-orange-50">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Evidence Documentation</h3>
                <p className="text-xs text-gray-600">Upload supporting documents or provide detailed notes to justify your selected maturity level</p>
              </div>
            </div>

            {/* Evidence Notes */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Evidence Notes *
              </label>
              <textarea
                value={evidenceNotes[currentDimension] || ''}
                onChange={(e) => setEvidenceNotes(prev => ({ ...prev, [currentDimension]: e.target.value }))}
                placeholder="Describe the evidence that supports your score. Examples:&#10;- Meeting minutes showing safety walks&#10;- Dashboard screenshots with KPIs&#10;- Training completion reports&#10;- Audit results&#10;- Policy documents"
                className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
                rows="4"
              />
              <p className="text-xs text-amber-700 mt-1">
                <strong>Required:</strong> Provide specific evidence that demonstrates your organization meets the selected maturity level criteria.
              </p>
            </div>

            {/* Evidence File Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Evidence Files (Optional)
              </label>

              {/* Uploaded Files List */}
              {(uploadedFiles[currentDimension] || []).length > 0 && (
                <div className="mb-3 space-y-2">
                  {uploadedFiles[currentDimension].map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between bg-white border border-amber-200 rounded-lg px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        {getFileIcon(file.content_type)}
                        <span className="text-sm text-gray-700 truncate max-w-xs">{file.filename}</span>
                        <span className="text-xs text-gray-500">({formatFileSize(file.file_size)})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeUploadedFile(currentDimension, file.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Area */}
              <div className="border-2 border-dashed border-amber-300 rounded-lg p-4 bg-white hover:border-amber-400 transition-colors">
                <div className="text-center">
                  {uploading ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                      <p className="mt-2 text-sm text-gray-600">Uploading...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="mx-auto h-10 w-10 text-amber-400" />
                      <div className="mt-2">
                        <label className="cursor-pointer">
                          <span className="text-sm font-medium text-amber-600 hover:text-amber-700">
                            Click to upload files
                          </span>
                          <input
                            type="file"
                            multiple
                            onChange={handleFileUpload}
                            className="hidden"
                            accept=".pdf,.docx,.xlsx,.pptx,.jpg,.jpeg,.png,.gif,.doc,.xls"
                          />
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                          PDF, Word, Excel, PowerPoint, or Images (max 50MB each)
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Evidence Checklist */}
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-medium text-blue-900 mb-2">💡 Evidence Best Practices:</p>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>✓ Reference specific dates and data points</li>
                <li>✓ Include quantitative metrics when available</li>
                <li>✓ Mention stakeholders or teams involved</li>
                <li>✓ Link to existing platform data (PTW, Training, CAPA records)</li>
              </ul>
            </div>
          </div>

          {/* Navigation */}
          <div className="p-6 border-t flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
              disabled={currentStep === 0}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            <Button
              onClick={saveDimensionScore}
              disabled={!selectedScore || loading || uploading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : currentStep === DIMENSIONS.length - 1 ? (
                <>
                  Complete & Generate Roadmap
                  <Sparkles className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default SSEMAssessmentWizard;
